import { C } from '@ridgewill/brand';
import { adminApi, type FeedEvent } from '../lib/api.js';
import { useAsync } from '../lib/useAsync.js';
import { exact } from '../lib/format.js';
import { RecordScreen } from '../components/Record.js';
import {
  AsyncBoundary,
  Badge,
  Button,
  Identity,
  Panel,
  PanelBody,
  When,
} from '../components/ui.js';

/**
 * One activity event, in full (#33).
 *
 * The list answers "what happened, roughly, and to whom". This answers "what
 * exactly", which for a `user.role-changed` row means the difference between
 * knowing a role changed and knowing which one.
 *
 * ── It renders the row it was handed, and used not to ───────────────
 *
 * `/admin/activity` omitted `detail`, `userAgent` and `archivedAt` while the
 * per-user endpoint returned them, so a modal built from the row alone showed a
 * subset of the line you clicked — which is not a drill-in. The workaround was
 * to fetch up to 200 of the subject's events and find the one already in hand.
 *
 * That had a real failure mode: an event further back than the window simply
 * could not be shown, on exactly the busy accounts most worth investigating,
 * and it cost a request per open to re-fetch data the list already had.
 *
 * RidgeWill/auth#201 made the feed return the same event columns as the
 * per-user view, so the fetch is gone. If a field ever reads empty here, the
 * question is what the FEED returned — there is no second source any more.
 */

export type DetailRow = { key: string; value: string };

/**
 * `detail` as flat rows, whatever shape it happens to be.
 *
 * ── Generic on purpose, and this is the load-bearing decision ────────
 *
 * The alternative is a `switch` over event types rendering each one nicely.
 * That is a second place to update every time auth adds a type, and — worse —
 * it shows NOTHING for a type it has not heard of, silently. auth owns the
 * vocabulary and adds to it without telling this app, so a renderer that only
 * handles what it knows is a renderer that quietly hides new information. Same
 * failure shape as a role list filtered to known roles (#28).
 *
 * So: every key is shown, including ones added after this was written.
 *
 * Arrays are joined rather than listed, because every array `detail` carries in
 * practice is a set of short tokens — role names, paths — and a bullet list of
 * two words each is worse than `user, keyodo-user`. An EMPTY array renders as
 * `none` rather than an empty cell, which matters immediately: auth#201's
 * proposed shape has `removed: []` on a pure addition, and a blank there would
 * read as missing rather than as "nothing was removed".
 *
 * Nested objects are JSON rather than recursed. Nothing produces them today,
 * and a flat table that occasionally shows a small JSON blob is more honest
 * than an indentation scheme invented for a case that may never arrive.
 *
 * PRESENTATION still varies, but on the value and never on the key — see
 * `isProse`. Long or multi-line values are stacked and wrapped instead of
 * clipped into a monospace cell. That keeps the "every key, including unknown
 * ones" guarantee above while letting auth start putting real text in here,
 * which it now does for the two `user.note-*` types.
 */
export function detailRows(detail: unknown): DetailRow[] {
  if (detail === null || detail === undefined) return [];
  if (typeof detail !== 'object' || Array.isArray(detail)) {
    return [{ key: 'detail', value: String(detail) }];
  }
  return Object.entries(detail as Record<string, unknown>).map(([key, value]) => ({
    key,
    value: renderValue(value),
  }));
}

function renderValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (Array.isArray(value)) return value.length ? value.map((v) => String(v)).join(', ') : 'none';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/**
 * Is this value PROSE rather than a token?
 *
 * `detail` used to be ids and short enum-ish strings, so one monospace line per
 * key was right for all of it. `user.note-added` and `user.note-removed` now
 * carry the note's text (RidgeWill/auth, org-admin#44) — up to 4000 characters
 * of somebody's typing, newlines included — and that in a 1fr monospace cell is
 * a single clipped line with the line breaks collapsed by HTML. The text is the
 * whole reason the field was added, so it has to be readable.
 *
 * Decided on the VALUE, never on the key or the event type. A `switch` on
 * `user.note-*` here would be the second place to update every time auth adds a
 * type, and this file exists partly to argue against exactly that. Anything
 * long or multi-line gets the readable treatment, whether or not this code has
 * heard of it.
 *
 * 120 characters is about two lines in the modal's 620px. Below it, a monospace
 * row is fine and switching would just make short values inconsistent.
 */
export function isProse(value: string): boolean {
  return value.includes('\n') || value.length > 120;
}

export function EventDetail({ id, backTo }: { id: string; backTo: string }) {
  const { state } = useAsync(() => adminApi.activityEvent(id), [id]);

  return (
    <AsyncBoundary state={state}>
      {(event) => <Event event={event} backTo={backTo} />}
    </AsyncBoundary>
  );
}

/**
 * STACKED PANELS, not tabs — Jon, 2026-08-28.
 *
 * The same call as the Agreements screen and for the same reason: an event is
 * one thing that happened, and "who did it, from where, and what changed" is a
 * single question. Splitting the detail off behind a tab would put the part
 * somebody opened this for one click away from the part that identifies it.
 */
function Event({ event, backTo }: { event: FeedEvent; backTo: string }) {
  const rows = detailRows(event.detail);

  return (
    <RecordScreen
      /* `backTo` rather than a constant `/activity`, so the feed's `?user=`
         survives the round trip. The Activity screen is KEYED on that parameter,
         so a back link that dropped it would remount the feed and throw away the
         window, the date range and the page somebody was working through — the
         state a modal used to preserve for free, and the one thing a screen has
         to be careful about. */
      back={{ to: backTo, label: 'Activity' }}
      /* The TYPE is the heading. It is the machine-readable name of what
         happened, and it is also what people say to each other about these rows
         — "a user.role-changed on that account" — so it is the name of the
         record rather than an identifier hidden in monospace. */
      title={event.type}
      description={<When iso={event.createdAt} />}
      badges={
        event.count > 1 ? (
          /* Collapsed repeats — one row standing for N occurrences inside the
             window. In the header rather than buried in a panel, because the
             whole screen otherwise reads as describing a single moment. */
          <Badge tone="warn">&times;{event.count}</Badge>
        ) : null
      }
      actions={
        <span style={{ display: 'flex', gap: '.5rem' }}>
          <Button href={`#/users/${encodeURIComponent(event.subject.id)}`}>View account</Button>
          {/* Only when there IS one. An event with no actor was done by the
              subject or the system, and a dead button is worse than no button. */}
          {event.actor ? (
            <Button href={`#/users/${encodeURIComponent(event.actor.id)}`}>View actor</Button>
          ) : null}
        </span>
      }
    >
      <Panel title="What happened">
        <PanelBody>
          <Rows>
            <Row label="Account">
              <Identity
                email={event.subject.email}
                name={event.subject.name}
                pseudonymized={event.subject.pseudonymized}
              />
            </Row>
            <Row label="Actor">
              {event.actor ? (
                <Identity email={event.actor.email} />
              ) : (
                /* `actorType` is what distinguishes "they did it themselves"
                   from "the actor's account has since been purged". */
                <Badge>{event.actorType}</Badge>
              )}
            </Row>
            <Row label="When">
              <span title={exact(event.createdAt)}>{exact(event.createdAt) ?? '—'}</span>
            </Row>
            <Row label="IP">
              <span style={mono}>{event.ipAddress ?? '—'}</span>
            </Row>
            <Row label="User agent">
              <span style={{ ...mono, wordBreak: 'break-word' }}>{event.userAgent ?? '—'}</span>
            </Row>
            {event.archivedAt ? (
              <Row label="Archived">
                <When iso={event.archivedAt} />
              </Row>
            ) : null}
            <Row label="Event id">
              <span style={{ ...mono, wordBreak: 'break-all' }}>{event.id}</span>
            </Row>
          </Rows>
        </PanelBody>
      </Panel>

      <Panel title="Detail">
        <PanelBody>
          {rows.length === 0 ? (
            <p style={note}>
              {/* Unambiguous, and worth saying: with nothing left to fetch, an
                  empty detail is a property of the event rather than a load that
                  did not happen. */}
              This event type records no structured detail.
            </p>
          ) : (
            <Rows>
              {rows.map((r) =>
                isProse(r.value) ? (
                  <Prose key={r.key} label={r.key} text={r.value} />
                ) : (
                  <Row key={r.key} label={r.key}>
                    <span style={mono}>{r.value}</span>
                  </Row>
                ),
              )}
            </Rows>
          )}
        </PanelBody>
      </Panel>
    </RecordScreen>
  );
}

function Rows({ children }: { children: React.ReactNode }) {
  return <div style={{ display: 'grid', gap: '.45rem', margin: '0 0 .9rem' }}>{children}</div>;
}

/**
 * A long or multi-line value, stacked instead of squeezed into the 1fr column.
 *
 * `pre-wrap` keeps the author's line breaks, which is the difference between a
 * note as they wrote it and one run together into a paragraph. It is rendered
 * as TEXT — React escapes it, and nothing here interprets markup — matching the
 * note field's own contract in auth ("plain text, never as markup").
 *
 * Capped in height and scrolled rather than allowed to run: 4000 characters
 * would otherwise push the modal's buttons off the bottom of the screen.
 */
function Prose({ label, text }: { label: string; text: string }) {
  return (
    <div style={{ display: 'grid', gap: '.3rem' }}>
      <span style={{ fontSize: '.82rem', fontWeight: 600, color: C.textMid }}>{label}</span>
      <div
        style={{
          fontSize: '.86rem',
          lineHeight: 1.5,
          color: C.text,
          whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere',
          maxHeight: '14rem',
          overflowY: 'auto',
          padding: '.5rem .6rem',
          // `C.page` against the modal's `C.surface`, the same inset pairing
          // the modal's own footer uses. `C.surface` here would be white on
          // white and the block would read as a stray border.
          background: C.page,
          border: `1px solid ${C.border}`,
          borderRadius: '.35rem',
        }}
      >
        {text}
      </div>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '9rem 1fr', gap: '.6rem', alignItems: 'baseline' }}>
      <span style={{ fontSize: '.82rem', fontWeight: 600, color: C.textMid }}>{label}</span>
      <span style={{ fontSize: '.88rem', color: C.text, minWidth: 0 }}>{children}</span>
    </div>
  );
}

const mono = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '.82rem' } as const;

const note = { margin: 0, fontSize: '.83rem', lineHeight: 1.5, color: C.muted } as const;
